<template>
  <Layout
    class="onboarding-product-demo16167"
    :class="{
      'onboarding-product-demo16167--loaded': riveRef?.isLoaded,
      [`onboarding-product-demo16167--step-${currentSection}`]:
        riveRef?.isLoaded,
    }"
    :title="page.title"
    :scrollTop="defaultSlotScroll"
  >
    <Transition name="fade" mode="out-in">
      <div
        v-if="showText"
        :key="currentText"
        class="onboarding-product-demo16167__text-wrapper"
      >
        <p class="onboarding-product-demo16167__text">
          {{ currentText }}
        </p>
      </div>
    </Transition>
    <Rive
      ref="riveRef"
      class="onboarding-product-demo16167__rive"
      :animations="`${currentSection}`"
      :src="getImage(animation.src)"
      :width="animation.width"
      :height="animation.height"
      @stop="nextSection"
    />
  </Layout>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'

import { getImage, QuestionnaireView } from '@/models/onboarding'
import { ProductDemo16167Page } from '@/models/onboarding.types'
import { platform } from '@/services/platform'

import Rive from '@/app/components/Rive.vue'
import Layout from '../Layout.vue'

const props = defineProps<{
  view: QuestionnaireView
}>()

const riveRef = ref<InstanceType<typeof Rive> | null>(null)

const showText = ref(false)

const page = computed(() => {
  return props.view.currentPage as ProductDemo16167Page
})

const currentSection = computed(() => {
  return props.view.currentSubpageIndex
})

const currentText = computed(() => {
  return page.value.content[currentSection.value]
})

const animation = computed(() => {
  return platform.isDesktopWidth()
    ? page.value.animationDesktop
    : page.value.animationMobile
})

const defaultSlotScroll = computed(() => {
  if (platform.isDesktopWidth()) {
    return 0
  }
  // This effectively anchors the Rive element
  // to the bottom of the container, as if the
  // container had `align-items: flex-end`, but
  // with a transition (`align-items` can't have
  // CSS transitions). The `9999` value is a high
  // enough number that should scroll to the bottom
  // on any screen.
  return currentSection.value >= 2 ? 9999 : 0
})

function nextSection(): void {
  showText.value = true

  // Wait for the text fade-out animation
  // before showing the continue buttton.
  setTimeout(() => {
    props.view.showContinueButton()
  }, 500)
}

watch(currentSection, () => {
  // When the user clicks "Continue",
  // hide the text and the "Continue"
  // button.
  if (!platform.isDesktopWidth()) {
    showText.value = false
  }
  props.view.hideContinueButton()
})
</script>

<style lang="scss" scoped>
$textWrapperHeight: 112px;
$pageTitleHeight: 60px;

.onboarding-product-demo16167 {
  overflow: hidden;
  // Extra space at the bottom to accomodate the footer
  // which is absolute positioned.
  padding-bottom: calc(var(--onboarding-padding) * 4);
  scroll-behavior: smooth;
  --page-title-height: 60px;

  &:deep(.onboarding-layout__default-slot) {
    position: relative;
    display: flex;
    justify-content: center;
    align-items: flex-start;
    @media (min-width: $w-tablet-b) {
      flex-direction: column;
      justify-content: flex-start;
      align-items: center;
    }
  }

  &:deep(.onboarding-layout__text) {
    height: $pageTitleHeight;
    @media (min-width: $w-tablet-b) {
      max-width: 520px;
      height: auto;
    }
  }

  &:deep(.onboarding-layout__title) {
    @media (min-width: $w-medium-b) {
      font-size: 24px;
      line-height: 30px;
    }
  }

  &__text-wrapper {
    position: absolute;
    left: 50%;
    top: var(--text-position-Y);
    z-index: 1;
    display: flex;
    justify-content: center;
    align-items: center;
    width: calc(100% + 24px);
    min-height: $textWrapperHeight;
    padding: 16px 24px;
    border-radius: 24px;
    border: 1px solid $c-black-2;
    background: $c-black-15;
    transform: translateX(-50%);
    // The steps transition makes it so that
    // the value is only updated at the end
    // of the transition (ie. after the element
    // is hidden).
    transition:
      opacity 0.5s,
      top 0.5s steps(1, end);
    @media (min-width: $w-tablet-b) {
      position: static;
      max-width: 520px;
      min-height: 102px;
      margin-block: 20px 48px;
      padding: 24px 40px;
      transform: none;
    }
  }

  &__text {
    font-size: 20px;
    line-height: 26px;
    font-weight: 500;
    text-align: center;
    letter-spacing: -0.02em;
    color: $c-white;
  }

  &__rive {
    flex-shrink: 0;
    width: calc(var(--rive-width) * 1px);
    height: calc(var(--rive-height) * 1px);
    transform: translateY(var(--rive-position-Y, 100%));
    transition: transform 1s;
    @media (min-width: $w-tablet-b) {
      max-width: none;
      transform: none;
    }
  }

  &:deep(~ .onboarding-questionnaire__button-wrapper) {
    position: absolute;
    bottom: 0;
    z-index: 99;
  }
}

.onboarding-product-demo16167--step-0 {
  --text-position-Y: 12px;
  --rive-position-Y: #{$textWrapperHeight + 24px};
}

.onboarding-product-demo16167--step-1 {
  --text-position-Y: 0;
  --rive-position-Y: #{$textWrapperHeight + 12px};
}

.onboarding-product-demo16167--step-2 {
  --text-position-Y: 70%;
  --rive-position-Y: 0;
}

.onboarding-product-demo16167--step-3 {
  --text-position-Y: 70%;
  --rive-position-Y: 0;
}

.onboarding-product-demo16167--step-4 {
  --text-position-Y: 80%;
  --rive-position-Y: #{$textWrapperHeight * -1 - 36px};
}

:not(.onboarding-product-demo16167--step-0) {
  @media (max-width: $w-tablet-s) {
    &:deep(.onboarding-layout__text) {
      height: 0;
      opacity: 0;
      transition: all 0.6s;
    }
  }
}
</style>
